const parseProviderDate = (value, fallbackDate) => {
  if (!value) return null

  const normalizedValue = String(value).replace(/\//g, '-').replace(' ', 'T')
  if (/^\d{2}:\d{2}/.test(normalizedValue) && fallbackDate) {
    return new Date(`${fallbackDate}T${normalizedValue}`)
  }

  return new Date(normalizedValue)
}

const normalizeObjectValue = (value, fields) => {
  if (!value || typeof value !== 'object') return value

  const field = fields.find(key => value[key])
  return field ? value[field] : ''
}

const normalizeAerodromeCode = value => normalizeObjectValue(value, ['code', 'iata_code', 'iata']) || value

const normalizeAircraft = value => normalizeObjectValue(value, ['name', 'title', 'code']) || value || ''

const normalizeNotification = notification => {
  if (!notification) return ''
  if (Array.isArray(notification)) return notification.filter(Boolean).join(' ')
  if (typeof notification === 'object') return notification.message || notification.text || ''
  return String(notification)
}

const normalizeFare = fare => {
  const calculatedPrice = fare.calculatedPrice || {}
  const fareClass = fare.fare_class || {}
  const rawTotal = calculatedPrice.segmentTotal
  const segmentTotal = (typeof rawTotal === 'number' || (typeof rawTotal === 'string' && rawTotal.trim())) ? Number(rawTotal) : NaN

  return {
    id: fare.fareid,
    offerId: fare.offer_id || null,
    flightId: fare.flightid,
    code: fare.classCode,
    name: fare.className,
    cabin: fare.cabinClass,
    seats: fare.freeseats,
    baggageAllowance: Number(fare.baggageAllowance),
    baggageUnit: fare.baggageUnit,
    baggage: `${fare.baggageAllowance} ${fare.baggageUnit}`,
    currency: calculatedPrice.currency,
    segmentTotal,
    estimatedAt: fareClass.estimated_at || null,
    estimatedPtl: fareClass.estimated_ptl || null,
    estimatedTtl: fareClass.estimated_ttl || null,
    calculatedPrice,
    notification: normalizeNotification(fare.notification)
  }
}

export const normalizeDeeplink = (payload, fallbackDates) => {
  let flights = payload?.aerocrs?.flights?.flight
    || payload?.flights?.flight
    || payload?.flights
    || payload?.results
    || payload?.data
    || (Array.isArray(payload) ? payload : [])
  if (!Array.isArray(flights)) flights = Object.values(flights || {})

  return flights.map((flight, index) => {
    const fares = Object.values(flight.classes || {}).map(normalizeFare).sort((first, second) => {
      const firstPriced = Number.isFinite(first.segmentTotal)
      const secondPriced = Number.isFinite(second.segmentTotal)
      if (firstPriced && secondPriced) return first.segmentTotal - second.segmentTotal
      if (firstPriced !== secondPriced) return firstPriced ? -1 : 1
      return 0
    })
    const pricedFares = fares.filter(fare => Number.isFinite(fare.segmentTotal))
    const availableFares = fares.filter(fare => fare.seats > 0)
    const cheapestFare = pricedFares.reduce((cheapest, fare) => {
      if (!cheapest || fare.segmentTotal < cheapest.segmentTotal) return fare
      return cheapest
    }, null)
    const minimumBaggageFare = availableFares.reduce((minimum, fare) => {
      if (!minimum || fare.baggageAllowance < minimum.baggageAllowance) return fare
      return minimum
    }, null)
    const direction = flight.direction || 'outbound'
    const fallbackDate = typeof fallbackDates === 'string' ? fallbackDates : direction === 'inbound' ? fallbackDates?.inbound : fallbackDates?.outbound
    const departure = parseProviderDate(flight.STD || flight.departure, fallbackDate)
    const arrival = parseProviderDate(flight.STA || flight.arrival, fallbackDate)
    const airline = flight.airline || {}
    const airlineCode = normalizeObjectValue(airline, ['code', 'iata_code', 'designator'])

    if (arrival && departure && arrival < departure) arrival.setDate(arrival.getDate() + 1)

    return {
      id: `${flight.fltnum || flight.number || flight.flight_code}-${index}`,
      flightCode: flight.flight_code,
      direction,
      rules: Array.isArray(flight.rules) ? flight.rules.filter(Boolean) : flight.rules ? [flight.rules] : [],
      airlineId: airline.id || flight.airline_id || null,
      createBooking: airline.create_booking === true,
      airline: flight.airlineName || normalizeObjectValue(airline, ['name', 'title']) || airlineCode || 'Unknown airline',
      logo: flight.airlineLogoUrl || airline.logo_url || flight.airlineLogo || airline.logo || airline.image || '',
      flightNumber: flight.fltnum || flight.number || flight.flight_code,
      fromId: flight.from?.id,
      toId: flight.to?.id,
      from: flight.provider_route?.from_code || flight.from?.name || normalizeAerodromeCode(flight.fromcode || flight.from),
      to: flight.provider_route?.to_code || flight.to?.name || normalizeAerodromeCode(flight.tocode || flight.to),
      departure,
      arrival,
      duration: arrival && departure ? arrival - departure : 0,
      via: typeof flight.via === 'string' ? flight.via.trim() : flight.via || '',
      type: flight.flighttype || flight.type,
      aircraft: normalizeAircraft(flight.aircraftType),
      fares,
      minPrice: cheapestFare ? cheapestFare.segmentTotal : 0,
      minPriceCurrency: cheapestFare?.currency || '',
      priceOptionsCount: pricedFares.length,
      minBaggage: minimumBaggageFare?.baggage || '',
      baggageOptionsCount: availableFares.length,
      maxSeats: fares.length ? Math.max(...fares.map(fare => fare.seats)) : 0
    }
  })
}
